import { Link } from "react-router-dom";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import { BUSINESS_INFO } from "../../content/site";

/**
 * TGL Affiliate Program Terms & Conditions.
 *
 * These are the terms a Global Link partner explicitly agrees to when
 * submitting the TGL application (referenced from the consent checkbox on
 * /global-link). Distinct from the general site Terms of Service at /terms.
 */
export default function TglTerms() {
  const updated = "February 2026";
  return (
    <div data-testid="page-tgl-terms">
      <section className="bg-altura-espresso text-altura-ivory pt-24 pb-20 relative noise overflow-hidden">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Wordmark className="text-altura-ivory" />
          <Eyebrow className="text-altura-gold mt-8">The Global Link · Legal</Eyebrow>
          <EditorialHeading size="lg" className="mt-4 text-altura-ivory">
            TGL Affiliate Program — Terms &amp; Conditions
          </EditorialHeading>
          <GoldDivider className="mt-6" />
          <p className="mt-6 text-altura-ivory/70">Last updated · {updated}</p>
        </div>
      </section>

      <section className="bg-altura-ivory py-20">
        <div className="container-editorial max-w-3xl legal-prose">
          <Section title="1. About TGL">
            The Global Link ("<strong>TGL</strong>") is the official affiliate and referral partner program of Altura Global Real Estate. It is designed for entrepreneurs, professionals, and global connectors who wish to introduce clients, investors, and buyers to Altura Global's international property opportunities and be compensated for successful referrals.
          </Section>

          <Section title="2. Eligibility & application">
            To join TGL you must be at least 18 years of age, legally able to enter into a binding agreement in your jurisdiction, and complete our online application. Altura Global reserves the right, in its sole discretion, to accept, defer, or decline any application. Applications are reviewed personally by our team.
          </Section>

          <Section title="3. Your referral code">
            Upon approval, you will receive a unique alphanumeric <strong>referral code</strong> (e.g. <code>NAME-A1B2</code>) and a personal referral link. The referral code identifies leads and clients you introduce to Altura Global. You are responsible for the confidentiality of your code and for how you share it. Referral codes are non-transferable.
          </Section>

          <Section title="4. Commission — how it works">
            <ul>
              <li>You earn a flat <strong>USD $250</strong> commission for each successful referral that results in a <strong>closed sale</strong> of an Altura Global property.</li>
              <li>A referral is considered "successful" only when: (i) the client explicitly identifies you or uses your referral code/link at first contact, (ii) the client transacts on an Altura Global opportunity, and (iii) the transaction fully closes (contract executed, funds received, ownership transferred).</li>
              <li>Commissions are paid <strong>30 days after the closed sale</strong>, using the payout method you have provided in your application (Bank Transfer, PayPal, Zelle, Cash App, or Other).</li>
              <li>You are responsible for ensuring your payout details (bank account, PayPal email, Zelle handle, etc.) are accurate. Altura Global is not responsible for failed or delayed payments caused by incorrect information supplied by you.</li>
              <li>You are solely responsible for any taxes, fees, or obligations arising from commissions you receive.</li>
            </ul>
          </Section>

          <Section title="5. What does NOT qualify for commission">
            <ul>
              <li>Self-referrals (referring yourself or an immediate family member for your own purchase);</li>
              <li>Referrals of clients who were already in active discussion with Altura Global prior to your introduction;</li>
              <li>Transactions that are cancelled, rescinded, refunded, or reversed for any reason;</li>
              <li>Referrals introduced through prohibited channels (see Section 7).</li>
            </ul>
          </Section>

          <Section title="6. Your obligations">
            As a TGL partner you agree to:
            <ul>
              <li>Represent Altura Global honestly, professionally, and consistent with our brand values;</li>
              <li>Only share truthful information about our services, opportunities, and commission terms;</li>
              <li>Not offer or promise anything on our behalf that has not been formally published by Altura Global;</li>
              <li>Comply with all applicable laws and regulations in the jurisdictions in which you operate, including advertising, consumer protection, anti-spam, and data-protection laws.</li>
            </ul>
          </Section>

          <Section title="7. Prohibited conduct">
            The following will result in immediate termination of your TGL status and forfeiture of unpaid commissions:
            <ul>
              <li>Sending unsolicited emails, SMS, or other messages (spam) using your referral link;</li>
              <li>Bidding on Altura Global branded terms in paid search or misrepresenting yourself as Altura Global on any channel;</li>
              <li>Creating fake accounts, self-referring, or otherwise engaging in fraudulent activity;</li>
              <li>Publishing false, misleading, or defamatory content about Altura Global, its opportunities, or its team;</li>
              <li>Any conduct that damages the reputation of Altura Global.</li>
            </ul>
          </Section>

          <Section title="8. Confidentiality">
            You may receive advance information about opportunities, partners, or business plans that are not yet public. You agree to keep such information confidential and to use it solely for the purpose of making qualified referrals to Altura Global.
          </Section>

          <Section title="9. No employment relationship">
            TGL is an independent-contractor referral relationship. You are not an employee, agent, joint-venturer, or franchisee of Altura Global. You have no authority to bind Altura Global to any obligation or commitment. You will bear your own expenses in connection with your participation in TGL.
          </Section>

          <Section title="10. Term & termination">
            TGL participation continues until terminated by either party. Altura Global may pause, suspend, or terminate your participation at any time for a material breach of these terms, prohibited conduct, or the discontinuation of the program. Upon termination, unpaid commissions for referrals that have already closed will still be paid according to Section 4. All other unearned commissions are forfeit.
          </Section>

          <Section title="11. Program changes">
            Altura Global may update the TGL program — including commission amounts, payout timing, and these terms — from time to time. Material changes will be communicated by email at least 15 days in advance. Continued participation in TGL after the effective date of a change constitutes acceptance of the updated terms.
          </Section>

          <Section title="12. Limitation of liability">
            To the fullest extent permitted by law, Altura Global will not be liable to you for any lost profits, lost opportunities, or indirect, incidental, consequential, or punitive damages arising from your participation in TGL. Our maximum aggregate liability to you under these terms will not exceed the total commissions paid or payable to you in the 12 months preceding the claim.
          </Section>

          <Section title="13. Governing law">
            These TGL terms are governed by the laws of the jurisdiction in which Altura Global Real Estate is principally domiciled. Any disputes will be resolved in the courts of competent jurisdiction in that domicile.
          </Section>

          <Section title="14. Relationship to the site Terms of Service">
            These TGL Terms &amp; Conditions supplement — and do not replace — the general{" "}
            <Link to="/terms" className="underline text-altura-espresso decoration-altura-gold hover:text-altura-gold-dark">Site Terms of Service &amp; Privacy Notice</Link>, which also apply to your use of the Site and your data.
          </Section>

          <Section title="15. Contact">
            Questions about TGL, your commission, or these terms? Reach us at{" "}
            <a href={`mailto:${BUSINESS_INFO.email}`} className="underline text-altura-espresso decoration-altura-gold">{BUSINESS_INFO.email}</a>.
          </Section>

          <div className="mt-16 rounded-lg border border-altura-gold/40 bg-altura-gold/10 p-6 text-sm">
            <p className="text-altura-espresso font-medium">
              By submitting the TGL application on <Link to="/global-link" className="underline decoration-altura-gold hover:text-altura-gold-dark">/global-link</Link>, you confirm that you have read, understood, and agree to be bound by these TGL Terms &amp; Conditions.
            </p>
          </div>

          <div className="mt-10 border-t border-altura-espresso/10 pt-8 text-sm text-altura-charcoal/60">
            © {new Date().getFullYear()} Altura Global Real Estate. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 first:mt-0">
      <h2 className="font-editorial text-2xl text-altura-espresso leading-tight">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] text-altura-charcoal/80 leading-[1.75]">
        {children}
      </div>
    </section>
  );
}
